import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  TActiveFilters,
  TCourseFilters,
  TCourseGroup,
  TCoursePreview,
  TNamedItem,
} from '../../types';

interface LibraryState {
  allCourses: TCourseGroup[];
  filteredCourses: TCourseGroup[];
  filters: TCourseFilters;
  secondaryFilters: TNamedItem[];
  stackFilters: TNamedItem[];
  activeFilters: TActiveFilters;
  activeFilterType: 'categories' | 'stacks';
}

const initialState: LibraryState = {
  allCourses: [],
  filteredCourses: [],
  secondaryFilters: [],
  stackFilters: [],
  filters: {
    parentCategories: [],
    categories: [],
    competencies: [],
    stacks: [],
  },
  activeFilters: {
    primaryFilter: null,
    secondaryFilter: null,
  },
  activeFilterType: 'categories',
};

export const librarySlice = createSlice({
  name: 'library',
  initialState,
  reducers: {
    setCourses(state, { payload }: PayloadAction<TCourseGroup[]>) {
      state.allCourses = payload;
      state.filteredCourses = filterCourseGroups(state);
    },
    setCourseFilters(state, { payload }: PayloadAction<TCourseFilters>) {
      state.filters = payload;
      if (!state.secondaryFilters.length) {
        state.secondaryFilters = payload.competencies;
      }
      state.stackFilters = payload.stacks;
    },
    setSecondaryFilters(state, { payload }: PayloadAction<TNamedItem>) {
      if (payload.id == state.filters.parentCategories[0].id) {
        state.secondaryFilters = state.filters.competencies;
      } else {
        state.secondaryFilters = state.filters.categories;
      }
    },
    filterCourses(state, { payload }: PayloadAction<Partial<TActiveFilters>>) {
      state.activeFilters = { ...state.activeFilters, ...payload };

      // If a secondaryFilter is set and it's a stack filter, update activeFilterType
      if (payload.secondaryFilter) {
        // Check if the filter is a stack filter by checking if it exists in stackFilters
        const isStackFilter = state.stackFilters.some(
          (stack) => stack.id === payload.secondaryFilter?.id,
        );

        if (isStackFilter) {
          state.activeFilterType = 'stacks';
        } else {
          state.activeFilterType = 'categories';
        }
      }

      state.filteredCourses = filterCourseGroups(state);
    },
    setActiveFilterType(
      state,
      { payload }: PayloadAction<'categories' | 'stacks'>,
    ) {
      state.activeFilterType = payload;
    },
  },
});

const filterCourseGroups = (state: LibraryState) => {
  if (state.activeFilterType === 'stacks') {
    // Check if a specific stack filter is selected
    if (state.activeFilters.secondaryFilter?.name) {
      const selectedStackName = state.activeFilters.secondaryFilter.name;
      const coursesInSelectedStack: TCoursePreview[] = [];

      // Iterate through all courses to find those matching the predicate and the selected stack
      state.allCourses.forEach((courseGroup) => {
        courseGroup.courses.forEach((course) => {
          if (
            coursePredicate(course, state) &&
            course.skillStacks?.includes(selectedStackName)
          ) {
            coursesInSelectedStack.push(course);
          }
        });
      });

      // Create a single group for the selected stack
      const selectedStackGroup: TCourseGroup = {
        groupName: selectedStackName,
        courses: coursesInSelectedStack,
      };

      // Return an array containing only the selected stack group
      return [selectedStackGroup];
    } else {
      // If no specific stack filter is selected, retain previous logic (group by all stacks in filtered courses)
      const stackGroups: { [key: string]: TCoursePreview[] } = {};

      // Iterate through all courses and group by stack
      state.allCourses.forEach((courseGroup) => {
        courseGroup.courses.forEach((course) => {
          if (coursePredicate(course, state)) {
            course.skillStacks?.forEach((stackName) => {
              if (!stackGroups[stackName]) {
                stackGroups[stackName] = [];
              }
              stackGroups[stackName].push(course);
            });
          }
        });
      });

      // Convert grouped courses into TCourseGroup array
      const sorted = Object.keys(stackGroups)
        .map((stackName) => ({
          groupName: stackName,
          courses: stackGroups[stackName],
        }))
        .sort((a, b) => b.courses.length - a.courses.length); // Sort by course count

      // The logic to bring the selected stack group to the top is now handled by the outer if block
      // when a secondary filter is present. This part is redundant in the else block.

      return sorted;
    }
  } else {
    // activeFilterType === 'categories'
    const sorted = state.allCourses
      .reduce((acc: TCourseGroup[], courseGroup) => {
        const filteredCourses = courseGroup.courses.filter((course) =>
          coursePredicate(course, state),
        );
        if (filteredCourses.length) {
          acc.push({
            groupName: courseGroup.groupName,
            courses: filteredCourses,
          });
        }
        return acc;
      }, [])
      .sort((a, b) => b.courses.length - a.courses.length);

    // Bring the selected category/competency group to the top
    if (state.activeFilters.secondaryFilter) {
      const index = sorted.findIndex(
        (group) =>
          group.groupName === state.activeFilters.secondaryFilter?.name,
      );
      if (index > 0) {
        sorted.unshift(sorted.splice(index, 1)[0]);
      }
    }

    return sorted;
  }
};

const coursePredicate = (course: TCoursePreview, state: LibraryState) => {
  let matches = true;
  const { activeFilters, stackFilters } = state;

  if (activeFilters.primaryFilter) {
    matches &&= course.categoryIds?.includes(activeFilters.primaryFilter.id);
  }

  if (activeFilters.secondaryFilter) {
    const secondaryFilter = activeFilters.secondaryFilter;

    // Check if the filter is a stack filter by checking if its ID exists in stackFilters
    const isStackFilter = stackFilters.some(
      (stack) => stack.id === secondaryFilter.id,
    );

    if (isStackFilter) {
      // Handle potential undefined from course.skillStacks?.includes
      matches &&= course.skillStacks?.includes(secondaryFilter.name) ?? false;
    } else {
      // Handle potential undefined from includes
      matches &&=
        (course.categoryIds?.includes(secondaryFilter.id) ?? false) ||
        (course.skillIds?.includes(secondaryFilter.id) ?? false);
    }
  }
  return matches;
};

export const {
  setCourses,
  setCourseFilters,
  setSecondaryFilters,
  filterCourses,
  setActiveFilterType,
} = librarySlice.actions;
export default librarySlice.reducer;
